import React from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, MessageCircle, ArrowRight, Check } from "lucide-react";
import { PUNTO_PARTIDA, DIRECTORY_CONSENT } from "@/lib/directoryConfig";
import FormBlock from "@/components/directory/FormBlock";
import PhotoDrop from "@/components/directory/PhotoDrop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none focus:ring-0 py-3 px-0 font-sans text-base text-[#3F403A] placeholder:text-[#3F403A]/35 transition-colors rounded-none";

const Field = ({ label, hint, children, testId }) => (
  <div data-testid={testId}>
    <label className="block font-serif text-xl md:text-2xl text-[#3F403A] leading-tight">
      {label}
    </label>
    {hint && <p className="mt-1 font-sans text-sm text-[#3F403A]/55">{hint}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const EMPTY = {
  nombre: "",
  dedicacion: "",
  punto_partida: "",
  solucionas: "",
  aportas: "",
  buscas: "",
  curiosidad: "",
  email: "",
  canal_tipo: "",
  canal_usuario: "",
  web: "",
};

export default function Registro() {
  const [searchParams] = useSearchParams();
  const editKey = searchParams.get("edit") || "";

  const [fields, setFields] = React.useState(EMPTY);
  const [photo, setPhoto] = React.useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = React.useState(null);
  const [consent, setConsent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [loadingEdit, setLoadingEdit] = React.useState(Boolean(editKey));
  const [editError, setEditError] = React.useState("");
  const [result, setResult] = React.useState(null); // { key, directory_url } after create

  const set = (k) => (e) =>
    setFields((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // ---- Edit mode: prefill from the personal key ----
  React.useEffect(() => {
    if (!editKey) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/attendees/me`, {
          params: { llave: editKey },
        });
        setFields({
          nombre: data.nombre || "",
          dedicacion: data.dedicacion || "",
          punto_partida: data.punto_partida || "",
          solucionas: data.solucionas || "",
          aportas: data.aportas || "",
          buscas: data.buscas || "",
          curiosidad: data.curiosidad || "",
          email: data.email || "",
          canal_tipo: data.canal_tipo || "",
          canal_usuario: data.canal_usuario || "",
          web: data.web || "",
        });
        setExistingPhotoUrl(`${API}${data.photo_url}?llave=${encodeURIComponent(editKey)}`);
        setConsent(true);
      } catch {
        setEditError("Tu llave de edición no es válida o ha caducado. Revisa el enlace que guardaste.");
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [editKey]);

  // ---- Submit ----
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!editKey && !photo) {
      toast.error("Sube tu foto: aparecerá en tu tarjeta del directorio.");
      return;
    }
    if (!fields.punto_partida) {
      toast.error("Elige la frase que mejor te describe hoy.");
      return;
    }
    if (!consent) {
      toast.error("Necesitamos tu consentimiento para publicar tu ficha.");
      return;
    }

    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v || ""));
    if (photo) fd.append("photo", photo);

    setSubmitting(true);
    try {
      if (editKey) {
        fd.append("llave", editKey);
        const { data } = await axios.put(`${API}/attendees/me`, fd);
        toast.success("Tu ficha se ha actualizado");
        window.location.href = `${window.location.origin}${data.directory_url}`;
        return;
      }
      fd.append("consentimiento", "true");
      const { data } = await axios.post(`${API}/attendees`, fd);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se ha podido enviar. Revisa los campos e inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Success screen (only after create) ----
  if (result) {
    const fullUrl = `${window.location.origin}${result.directory_url}`;
    const waText = encodeURIComponent(
      `Mi llave de acceso al directorio privado de Lidera con Voz Propia · Edición Primer Aniversario. La guardo a buen recaudo: ${fullUrl}`
    );
    return (
      <main className="min-h-screen bg-[#FAF8F3] text-[#3F403A]" data-testid="registro-success">
        <div className="max-w-2xl mx-auto px-6 py-20 md:py-28">
          <p className="overline text-[#63846F] mb-6">Registro completado</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05]">
            Ya formas parte
            <span className="italic text-[#63846F]"> de la sala.</span>
          </h1>
          <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/85">
            Este es tu enlace personal al directorio de asistentes. Es tu llave
            de acceso — guárdalo para consultar quién es quién y editar tu ficha
            cuando quieras.
          </p>

          <div
            className="mt-8 border border-[#63846F]/40 bg-[#F4EFE7] p-5 break-all font-sans text-sm text-[#3F403A]/85"
            data-testid="success-link"
          >
            {fullUrl}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="success-copy-btn"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(fullUrl);
                  toast.success("Enlace copiado");
                } catch {
                  toast.error("No se ha podido copiar — selecciónalo manualmente");
                }
              }}
              className="inline-flex items-center justify-center gap-3 border border-[#3F403A]/30 text-[#3F403A] hover:border-[#63846F] hover:text-[#63846F] transition-colors px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
            >
              <Copy className="h-4 w-4" strokeWidth={1.5} /> Copiar enlace
            </button>
            <a
              href={`https://wa.me/?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-whatsapp-btn"
              className="inline-flex items-center justify-center gap-3 border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Enviar a mi WhatsApp
            </a>
            <a
              href={result.directory_url}
              data-testid="success-enter-btn"
              className="inline-flex items-center justify-center gap-3 border border-transparent text-[#63846F] hover:underline px-7 py-3.5 uppercase tracking-[0.22em] text-xs font-medium"
            >
              Entrar al directorio <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>

          <p className="mt-8 font-sans text-sm italic text-[#3F403A]/60">
            Consejo: envíatelo por WhatsApp y así lo tendrás siempre a mano en tu chat.
          </p>
        </div>
      </main>
    );
  }

  // ---- Form ----
  return (
    <main className="min-h-screen bg-[#FAF8F3] text-[#3F403A]" data-testid="registro-page">
      {/* Minimal editorial header */}
      <header className="border-b border-[#3F403A]/10">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" aria-label="Lidera con Voz Propia" className="block" data-testid="registro-logo">
            <img src="/logo-lidera.png" alt="Lidera con Voz Propia" className="h-12 md:h-14 w-auto" />
          </a>
          <p className="overline text-[#3F403A]/60">Directorio privado</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <p className="overline text-[#63846F] mb-6">Edición Primer Aniversario · 15 de octubre · Sevilla</p>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.02]" data-testid="registro-title">
          {editKey ? (
            <>Tu ficha del <span className="italic text-[#63846F]">directorio.</span></>
          ) : (
            <>Preséntate <span className="italic text-[#63846F]">a la sala.</span></>
          )}
        </h1>
        <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/80 max-w-xl">
          Cinco minutos para tu tarjeta del directorio, tu círculo de llegada y
          tus primeras conexiones. Solo lo verán las asistentes de esta edición.
        </p>

        {loadingEdit && (
          <p className="mt-10 font-sans text-sm text-[#3F403A]/60" data-testid="registro-edit-loading">
            Cargando tu ficha…
          </p>
        )}
        {editError && (
          <p className="mt-10 border-l-2 border-[#B77A4B] pl-5 font-sans text-sm text-[#3F403A]/85" data-testid="registro-edit-error">
            {editError}
          </p>
        )}

        {!loadingEdit && !editError && (
          <form onSubmit={onSubmit} className="mt-14 md:mt-20 space-y-14 md:space-y-20" data-testid="registro-form">
            <FormBlock number="01" title="Quién eres" testId="registro-block-identidad">
              <Field label="Nombre y apellido" testId="registro-field-nombre">
                <input
                  className={inputCls}
                  value={fields.nombre}
                  onChange={set("nombre")}
                  required
                  maxLength={120}
                  placeholder="Cómo quieres aparecer en el directorio"
                  data-testid="field-nombre"
                />
              </Field>
              <Field label="A qué te dedicas" hint="Cómo quieres que te lean, en una línea" testId="registro-field-dedicacion">
                <input
                  className={inputCls}
                  value={fields.dedicacion}
                  onChange={set("dedicacion")}
                  required
                  maxLength={180}
                  placeholder="Ej. Fundadora y CEO de…"
                  data-testid="field-dedicacion"
                />
              </Field>
              <Field label="Tu foto" hint="Aparecerá en tu tarjeta del directorio" testId="registro-field-foto">
                <PhotoDrop file={photo} existingUrl={existingPhotoUrl} onChange={setPhoto} testId="photo-drop" />
              </Field>
            </FormBlock>

            <FormBlock
              number="02"
              title="Tu punto de partida"
              hint="Elige una: decidirá tu círculo de llegada al evento"
              testId="registro-block-punto"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PUNTO_PARTIDA.map((op) => {
                  const active = fields.punto_partida === op.id;
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => setFields((f) => ({ ...f, punto_partida: op.id }))}
                      aria-pressed={active}
                      data-testid={`punto-${op.id}`}
                      className={`text-left border p-6 transition-colors ${
                        active
                          ? "border-[#63846F] bg-[#63846F]/[0.06]"
                          : "border-[#3F403A]/15 bg-transparent hover:border-[#63846F]/60"
                      }`}
                    >
                      <span className={`overline ${active ? "text-[#63846F]" : "text-[#3F403A]/45"}`}>{op.id}</span>
                      <span className="mt-3 block font-serif text-lg md:text-xl leading-snug text-[#3F403A]">
                        {op.pregunta}
                      </span>
                      <span className="mt-3 block font-sans text-sm text-[#3F403A]/65 leading-relaxed">
                        {op.descripcion}
                      </span>
                      {active && (
                        <span className="mt-4 inline-flex items-center gap-2 overline text-[10px] text-[#63846F]">
                          <Check className="h-3.5 w-3.5" strokeWidth={2} /> Círculo {op.circulo}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </FormBlock>

            <FormBlock number="03" title="Tu capital" testId="registro-block-capital">
              <Field label="Lo que solucionas mejor que nadie" hint="En tu negocio, tu superpoder concreto" testId="registro-field-solucionas">
                <textarea className={`${inputCls} resize-none`} rows={2} value={fields.solucionas} onChange={set("solucionas")} required maxLength={500} data-testid="field-solucionas" />
              </Field>
              <Field label="Lo que puedes aportar a la sala" hint="No tu servicio, sino lo que puedes abrir a otra: una introducción, acceso a tu red, tu criterio sobre algo concreto" testId="registro-field-aportas">
                <textarea className={`${inputCls} resize-none`} rows={2} value={fields.aportas} onChange={set("aportas")} required maxLength={500} data-testid="field-aportas" />
              </Field>
              <Field label="Lo que buscas en este evento" hint="Concreto: una introducción, una aliada, un espacio…" testId="registro-field-buscas">
                <textarea className={`${inputCls} resize-none`} rows={2} value={fields.buscas} onChange={set("buscas")} required maxLength={500} data-testid="field-buscas" />
              </Field>
              <Field label="Una curiosidad sobre ti" hint="Para romper el hielo" testId="registro-field-curiosidad">
                <input className={inputCls} value={fields.curiosidad} onChange={set("curiosidad")} required maxLength={300} data-testid="field-curiosidad" />
              </Field>
            </FormBlock>

            <FormBlock number="04" title="Cómo pueden encontrarte" testId="registro-block-contacto">
              <Field label="Email de contacto" hint="Visible en el directorio" testId="registro-field-email">
                <input type="email" className={inputCls} value={fields.email} onChange={set("email")} required data-testid="field-email" />
              </Field>
              <Field label="Tu canal principal" hint="Opcional · Instagram o LinkedIn" testId="registro-field-canal">
                <div className="flex gap-3" role="group" aria-label="Canal principal">
                  {[
                    { id: "instagram", label: "Instagram" },
                    { id: "linkedin", label: "LinkedIn" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        setFields((f) => ({ ...f, canal_tipo: f.canal_tipo === c.id ? "" : c.id }))
                      }
                      aria-pressed={fields.canal_tipo === c.id}
                      data-testid={`canal-${c.id}`}
                      className={`px-6 py-3 border overline text-[11px] transition-colors ${
                        fields.canal_tipo === c.id
                          ? "border-[#63846F] text-[#63846F] bg-[#63846F]/[0.06]"
                          : "border-[#3F403A]/20 text-[#3F403A]/60 hover:border-[#63846F]/60"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                {fields.canal_tipo && (
                  <input
                    className={`${inputCls} mt-4`}
                    value={fields.canal_usuario}
                    onChange={set("canal_usuario")}
                    placeholder={fields.canal_tipo === "instagram" ? "Tu usuario (ej. @tuempresa)" : "Tu usuario o URL de perfil"}
                    maxLength={120}
                    data-testid="field-canal-usuario"
                  />
                )}
              </Field>
              <Field label="Tu web" hint="Opcional" testId="registro-field-web">
                <input className={inputCls} value={fields.web} onChange={set("web")} placeholder="https://…" maxLength={200} data-testid="field-web" />
              </Field>
            </FormBlock>

            <div className="border-t border-[#3F403A]/12 pt-10">
              {!editKey && (
                <label className="flex items-start gap-4 cursor-pointer select-none" data-testid="registro-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#63846F]"
                    data-testid="field-consent"
                  />
                  <span className="font-sans text-xs md:text-sm text-[#3F403A]/70 leading-relaxed">
                    {DIRECTORY_CONSENT}
                  </span>
                </label>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="registro-submit"
                className="mt-8 inline-flex items-center justify-center gap-3 border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-10 py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium w-full sm:w-auto"
              >
                {submitting
                  ? "Enviando…"
                  : editKey
                    ? "Guardar cambios"
                    : "Presentarme a la sala"}
                {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={1.5} />}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
